import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';

import { bem } from 'helpers/class-name';
import Message from 'components/Message';
import Button from 'components/Button';
import { intlMessage } from 'constants/propTypes';
import { ICONS } from 'constants/icons';
import LINKS from 'constants/links';

import './SmartSpotOnboarding.scss';

const { block, element } = bem('smart-spot-onboarding');

const messages = defineMessages({
  learnMorePeakTimes: {
    id: 'smart-spot-onboarding.learn-more-peak-times',
    defaultMessage: 'See details on Peak and Off-peak times <a>here</a>.',
  },
  footerText: {
    id: 'smart-spot-onboarding.footer-text',
    defaultMessage: 'You can edit this setting later.',
  },
  skipButton: {
    id: 'smart-spot-onboarding.skip-button',
    defaultMessage: 'Skip',
  },
});

const SmartSpotOnboarding = ({
  title,
  description,
  settingDetail,
  children,
  className,
  learnMoreLink,
  handleLearnMore,
  learnMoreLocation,
  illustration,
  showLearnMorePeakTimes,
  nextButton,
  onSkip,
  onNext,
  isSubmitting = false,
  nextButtonDisabled = false,
}) => {
  return (
    <div className={block(className)}>
      <div className={element('content-section')}>
        <div className={element('illustration-section')}>
          {illustration && <img className={element('illustration')} src={illustration} alt="SmartSpot illustration" />}
        </div>
        <div className={element('form-container')}>
          <div className={element('header')}>
            <div className={element('header-content')}>
              <Message as="h1" className={element('title')} message={title} />
              {description && (
                <Message
                  as="p"
                  className={element('description')}
                  message={description}
                  values={{
                    a: text => (
                      <a href={learnMoreLink} target="_blank" rel="noopener noreferrer">
                        {text}
                      </a>
                    ),
                  }}
                  onClick={() => handleLearnMore(learnMoreLocation)}
                />
              )}
            </div>
          </div>
          {settingDetail && (
            <div className={element('setting-info')}>
              <Isvg className={element('gear-icon')} src={ICONS.gears} />
              <Message as="p" message={settingDetail} />
            </div>
          )}
          <div className={element('content')}>{children}</div>
          {showLearnMorePeakTimes && (
            <Message
              className={element('learn-more-peak-times')}
              message={messages.learnMorePeakTimes}
              values={{
                a: text => (
                  <a href={LINKS.termsAdditionalDefinitions.href} target="_blank" rel="noopener noreferrer">
                    {text}
                  </a>
                ),
              }}
            />
          )}
          <div className={element('footer')}>
            <Message className={element('footer-text')} message={messages.footerText} />
            <div className={element('actions')}>
              <Button className={element('skip-button')} styled="secondary" onClick={onSkip}>
                <Message message={messages.skipButton} />
              </Button>
              {nextButton && (
                <Button
                  className={element('next-button')}
                  styled="primary"
                  onClick={onNext}
                  waiting={isSubmitting}
                  disabled={nextButtonDisabled || isSubmitting}>
                  <Message message={nextButton} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SmartSpotOnboarding.propTypes = {
  title: intlMessage.isRequired,
  description: intlMessage,
  settingDetail: intlMessage,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  learnMoreLink: PropTypes.string,
  handleLearnMore: PropTypes.func,
  learnMoreLocation: PropTypes.string,
  illustration: PropTypes.string,
  showLearnMorePeakTimes: PropTypes.bool,
  nextButton: intlMessage,
  onSkip: PropTypes.func,
  onNext: PropTypes.func,
  isSubmitting: PropTypes.bool,
  nextButtonDisabled: PropTypes.bool,
};

export default SmartSpotOnboarding;
